/*
 * WrittenInterview — a structured, typed interview (#/interviews/written).
 *
 * Questions come from the engine for the role's hub scope (or the chosen focus
 * areas, one per question in turn), at the chosen difficulty. Each answer is
 * graded by the engine's theory grader when submitted — which also records it
 * to learning memory — and the round ends in the shared feedback report.
 *
 * No timer: a written interview is about answer quality. Progress (questions,
 * drafts, grades) is auto-saved in this browser, so exiting and starting the
 * same written interview again resumes it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import APIService from '../../services/APIService';
import IvShell from './InterviewChrome';
import { indexTree } from './paths';
import { LENGTHS, roleOf, setupScope, writtenReport } from './interviewData';
import { IconArrowLeft, IconArrowRight, IconCheck } from './icons';

const DRAFT_KEY = 'pu_written_draft_v1';
const sigOf = (setup) => JSON.stringify([setup.roleId, (setup.focus || []).map((f) => f.id).sort(), setup.difficulty, setup.length]);
const loadDraft = (sig) => {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    return d && d.sig === sig && Date.now() - d.ts < 3 * 86400000 && (d.items || []).length ? d : null;
  } catch { return null; }
};

const WrittenInterview = ({ setup, hub, onExit, onComplete, ...shell }) => {
  const byId = useMemo(() => indexTree(hub || []), [hub]);
  const scope = useMemo(() => setupScope(setup, hub ? byId : null), [setup, hub, byId]);
  const total = (LENGTHS.find((l) => l.id === setup.length) || LENGTHS[1]).questions;
  const role = roleOf(setup.roleId);
  const sig = useMemo(() => sigOf(setup), [setup]);
  const nameOf = useCallback((cid) => {
    if (!cid) return '';
    const parts = String(cid).split('.');
    // the topic (skill.topic) reads best; deeper concept names are long
    for (let i = Math.min(2, parts.length); i > 0; i -= 1) { const n = byId[parts.slice(0, i).join('.')]; if (n) return n.name; }
    return parts[parts.length - 1].replace(/_/g, ' ');
  }, [byId]);

  const draft = useMemo(() => loadDraft(sig), [sig]);
  const [items, setItems] = useState(() => draft?.items || []);   // [{ q, answer, grade }]
  const [idx, setIdx] = useState(() => Math.min(draft?.idx || 0, (draft?.items || []).length));
  const [resumed, setResumed] = useState(!!draft);
  const [loadErr, setLoadErr] = useState('');
  const [grading, setGrading] = useState(false);
  const [gradeErr, setGradeErr] = useState('');
  const [confirmExit, setConfirmExit] = useState(false);
  const recentRef = useRef((draft?.items || []).map((it) => it.q.question));
  const fetchingRef = useRef(false);
  const t0 = useRef(Date.now() - (draft?.seconds || 0) * 1000);   // duration for the report only — never shown

  // Auto-save: questions, drafts and grades.
  useEffect(() => {
    if (!items.length) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ sig, items, idx, ts: Date.now(), seconds: Math.floor((Date.now() - t0.current) / 1000) }));
    } catch { /* storage full / private mode */ }
  }, [items, idx, sig]);

  // Keep exactly one question ahead of the learner, fetched in order (so "recent" de-duplicates).
  const fetchNext = useCallback(async (n) => {
    if (fetchingRef.current || !scope.length) return;
    fetchingRef.current = true;
    try {
      const focus = !setup.recommended && setup.focus.length ? [setup.focus[n % setup.focus.length].id] : scope;
      const q = await APIService.engineTheoryNext({ conceptIds: focus, recent: recentRef.current.slice(-10), difficulty: setup.difficulty || null });
      if (!q || !q.question) throw new Error('No question came back for this topic.');
      recentRef.current.push(q.question);
      setItems((prev) => (prev.length === n ? [...prev, { q, answer: '', grade: null }] : prev));
      setLoadErr('');
    } catch (e) {
      setLoadErr(e.message || 'Could not load the next question.');
    } finally {
      fetchingRef.current = false;
    }
  }, [scope, setup]);

  useEffect(() => {
    if (items.length < total && items.length <= idx + 1) fetchNext(items.length);
  }, [items.length, idx, total, fetchNext]);

  const cur = items[idx];
  const setAnswer = (v) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, answer: v } : it)));

  const submit = async () => {
    if (!cur || cur.grade || !cur.answer.trim() || grading) return;
    setGrading(true); setGradeErr('');
    try {
      const g = await APIService.engineTheoryGrade({
        question: cur.q.question, answer: cur.answer, conceptIds: cur.q.concept_ids || scope,
        itemId: cur.q.item_id || null, depth: cur.q.depth || null,
      });
      setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, grade: g } : it)));
    } catch (e) {
      setGradeErr(e.message || 'Grading failed — try submitting again.');
    } finally {
      setGrading(false);
    }
  };

  const finish = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    onComplete(writtenReport({ items, planned: total, nameOf, durationSeconds: Math.floor((Date.now() - t0.current) / 1000) }));
  };
  const startFresh = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    recentRef.current = []; setItems([]); setIdx(0); setResumed(false);
  };
  const answeredN = items.filter((it) => it.grade).length;
  const last = idx === total - 1;
  const words = cur ? (cur.answer.trim() ? cur.answer.trim().split(/\s+/).length : 0) : 0;
  const band = (s) => (s >= 80 ? 'good' : s >= 60 ? 'ok' : 'low');

  return (
    <IvShell hub={hub} className="ivx-written-page" {...shell}>
      <div className="ivx-whead">
        <div>
          <h1>Written Interview — {role?.title}</h1>
          <p>{setup.level}{setup.company ? ` · ${setup.company}` : ''} · {total} questions</p>
        </div>
        <div className="ivx-whead-acts">
          {answeredN > 0 && <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={finish}>Finish &amp; get feedback</button>}
          <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => setConfirmExit(true)}>Exit interview</button>
        </div>
      </div>
      {resumed && (
        <p className="ivx-resumed">Picked up where you left off. <button type="button" className="pu-link" onClick={startFresh}>Start fresh</button></p>
      )}

      <div className="ivx-written">
        <section className="pu-panel ivx-wq" aria-live="polite">
          {!cur ? (
            loadErr
              ? <p className="pu-empty">{loadErr} <button type="button" className="pu-link" onClick={() => fetchNext(items.length)}>Try again</button></p>
              : <p className="pu-empty">Preparing question {idx + 1}…</p>
          ) : (
            <>
              <div className="ivx-wq-head">
                <span className="ivx-wq-n">Question {idx + 1} of {total}</span>
                <span className="ivx-wq-topic">{nameOf((cur.q.concept_ids || [])[0])}</span>
              </div>
              <h2 className="ivx-wq-text">{cur.q.question}</h2>

              <label className="ivx-answer">
                <span className="pu-sr">Your answer</span>
                <textarea value={cur.answer} onChange={(e) => setAnswer(e.target.value)} readOnly={!!cur.grade}
                  placeholder="Write your answer as you would say it in an interview…" />
              </label>
              <div className="ivx-answer-meta"><span>{words} words</span><span>{cur.grade ? 'Submitted' : cur.answer ? 'Draft auto-saved' : ''}</span></div>

              {cur.grade && (
                <div className={`ivx-grade ${band(cur.grade.score)}`}>
                  <b>{cur.grade.score}<small>/100</small></b>
                  <span>
                    {(cur.grade.strengths || [])[0] && <><strong>Worked:</strong> {cur.grade.strengths[0]}<br /></>}
                    {(cur.grade.gaps || [])[0] && <><strong>Missing:</strong> {cur.grade.gaps[0]}</>}
                  </span>
                </div>
              )}
              {gradeErr && <p className="pu-empty">{gradeErr}</p>}

              <div className="ivx-wq-foot">
                <button type="button" className="pu-link ivx-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)}><IconArrowLeft size={13} /> Previous</button>
                <span className="ivx-grow" />
                {!cur.grade && !last && <button type="button" className="pu-btn pu-btn-quiet" disabled={grading} onClick={() => setIdx(idx + 1)}>Skip</button>}
                {!cur.grade && (
                  <button type="button" className="pu-btn pu-btn-primary" disabled={!cur.answer.trim() || grading} onClick={submit}>
                    {grading ? 'Reviewing your answer…' : <>Submit Answer <IconArrowRight size={14} /></>}
                  </button>
                )}
                {cur.grade && (last
                  ? <button type="button" className="pu-btn pu-btn-primary" onClick={finish}>Finish &amp; get feedback <IconArrowRight size={14} /></button>
                  : <button type="button" className="pu-btn pu-btn-primary" onClick={() => setIdx(idx + 1)}>Next Question <IconArrowRight size={14} /></button>)}
                {!cur.grade && last && answeredN > 0 && <button type="button" className="pu-btn pu-btn-quiet" onClick={finish}>Finish without this one</button>}
              </div>
            </>
          )}
        </section>

        <aside className="pu-panel ivx-wprog" aria-label="Interview progress">
          <h3>Interview progress</h3>
          <p className="pu-meta">{answeredN} / {total} answered</p>
          <span className="pu-bar"><i style={{ width: `${(answeredN / total) * 100}%` }} /></span>
          <ol>
            {Array.from({ length: total }, (_, i) => {
              const it = items[i];
              const state = it?.grade ? 'done' : i === idx ? 'cur' : it && i < idx ? 'skip' : 'todo';
              const label = it ? nameOf((it.q.concept_ids || [])[0]) || `Question ${i + 1}` : `Question ${i + 1}`;
              return (
                <li key={i}>
                  <button type="button" className={state} disabled={!it} title={!it ? 'Opens when you reach it' : undefined}
                    onClick={() => setIdx(i)} aria-current={i === idx ? 'step' : undefined}
                    aria-label={`${label} — ${state === 'done' ? 'answered' : state === 'cur' ? 'current' : state === 'skip' ? 'skipped' : 'upcoming'}`}>
                    <span className="ivx-dot">{state === 'done' ? <IconCheck size={11} /> : i + 1}</span>
                    <span>{label}</span>
                    {it?.grade ? <small>{it.grade.score}/100</small> : state === 'skip' ? <small>Skipped</small> : null}
                  </button>
                </li>
              );
            })}
          </ol>
          <p className="pu-meta ivx-wprog-note">You can go back to any question you skipped.</p>
        </aside>
      </div>

      {confirmExit && (
        <div className="kh-modal-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmExit(false); }}>
          <div className="kh-modal ivx-confirm" role="alertdialog" aria-modal="true" aria-labelledby="ivx-exit-h">
            <h2 id="ivx-exit-h">Exit this interview?</h2>
            <p>Your progress will be saved — start the same written interview again to pick up where you left off.</p>
            <div className="kh-modal-foot">
              <button type="button" className="pu-btn pu-btn-quiet" autoFocus onClick={() => setConfirmExit(false)}>Continue interview</button>
              <button type="button" className="pu-btn pu-btn-primary" onClick={onExit}>Exit interview</button>
            </div>
          </div>
        </div>
      )}
    </IvShell>
  );
};

export default WrittenInterview;
